<template>
  <div>
    <v-container class="ma-n3 pa-n3">
      <v-row no-gutters align="center" justify="space-between" :class="{ 'collapsed-heading': !expanded }" class="mr-n6">
        <h2 class="clickable" @click="expanded = !expanded">
          Notice History
          <v-icon v-if="expanded">arrow_drop_up</v-icon>
          <v-icon v-else>arrow_drop_down</v-icon>
        </h2>
        <v-spacer></v-spacer>
        <v-btn small tile class="mr-2" @click="showPhotoGallery" :disabled="!hasAnyPhotos"> <v-icon>mdi-image-outline</v-icon> View All Photos </v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small tile v-on="on"> <v-icon>save_alt</v-icon>&nbsp;Export </v-btn>
          </template>
          <v-list>
            <v-list-item @click="exportCsv()">
              <v-list-item-title> <v-icon>grid_on</v-icon>&nbsp;Excel (CSV) </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>
    </v-container>

    <v-expand-transition>
      <v-card v-show="expanded">
        <v-data-table :items="this.defects" :items-per-page="options.itemsPerPage" :sort-by="options.sortBy" :headers="headers" :loading="$wait.is(['loading defects'])" :footer-props.sync="footerProps" :options.sync="options">
          <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
          <template v-slot:item="props">
            <tr @click="!disabled ? handleEditDefectNotice(props.item.defectNoticeId) : null" :class="classes(props.item)">
              <td class="undefined">{{ props.item.ednNumber }}</td>
              <td>{{ moment(props.item.receivedDate).format(dateFormat) }}</td>
              <td v-if="props.item.notes">{{ props.item.description + ' // ' + props.item.notes }} <v-icon>pending_actions</v-icon> <v-icon v-if="props.item.hasPhotos">image</v-icon></td>
              <td v-else>{{ props.item.description }} <v-icon v-if="props.item.hasPhotos">image</v-icon></td>
              <td>{{ moment(props.item.completedDate).format(dateFormat) }}</td>
            </tr>
          </template>
          <template v-slot:no-results>
            <v-layout row align-center>
              <v-flex>No completed notices found</v-flex>
            </v-layout>
          </template>
        </v-data-table>
      </v-card>
    </v-expand-transition>
    <v-dialog persistent v-model="dialogs.showDefectScheduleEntry" :max-width="showCalendar || showLog || showChildLog ? '90vw' : '60vw'">
      <v-row no-gutters>
        <v-col :cols="showCalendar ? 9 : showLog || showChildLog ? 6 : 12">
          <schedule-form :value="model" ref="scheduleform" @delete-edn="deleteEdn($event.id, $event.source)">
            <template v-slot:header>
              <v-spacer></v-spacer>
              <v-btn @click="toggleLog" class="mr-2" color="accent" v-show="!showCalendar && !showChildLog && userIsFleetManager">
                Logs
                <v-icon small v-if="showLog">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
              <v-btn @click="toggleCalendar" color="accent" v-show="!showLog && !showChildLog">
                Calendar
                <v-icon small v-if="showCalendar">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
            </template>
            <template v-slot:actions>
              <v-btn @click="closeScheduleForm">Cancel</v-btn>
            </template>
          </schedule-form>
        </v-col>
        <v-col v-if="showLog && userIsFleetManager" cols="6">
          <defect-notice-audit-log :defect-notice-id="model.id"></defect-notice-audit-log>
        </v-col>
        <v-col v-if="showChildLog" cols="6">
          <defect-audit-log :defect-id="childLogDefectId" :defect-number="childLogDefectNumber" :work-sessions="childLogWorkSessions" :assignees="childLogAssignees" :historical-accepted-jobs="childLogHistoricalAcceptedJobs"></defect-audit-log>
        </v-col>
        <v-col v-show="showCalendar" cols="3">
          <calendar-preview ref="calendar"></calendar-preview>
        </v-col>
      </v-row>
    </v-dialog>

    <!-- Photo Gallery Modal -->
    <photo-gallery-modal ref="photoGalleryModal" :photos="galleryPhotos" />
  </div>
</template>

<script>
import moment from 'moment';
import { Event } from '@/plugins/bus';
import DefectNotices from '@/components/DefectNotices/DefectNoticesClient.vue';
import GarageLocationsClient from '@/components/GarageLocations/GarageLocationsClient.vue';
import ScheduleForm from '@/components/DefectNotices/DefectEntryDialogs/Step4ScheduleForm.vue';
import CalendarPreview from '@/components/DefectNotices/DefectEntryDialogs/CalendarPreview.vue';
import PhotoGalleryModal from '@/components/PhotoGalleryModal.vue';
import { DateFormat } from '@/plugins/date-filter';
import DownloadHelper from '@/lib/DownloadHelper';
import DefectAuditLog from '@/components/DefectNotices/DefectAuditLog.vue';
import DefectNoticeAuditLog from '@/components/DefectNotices/DefectNoticeAuditLog.vue';

export default {
  mixins: [GarageLocationsClient, DefectNotices],
  components: { DefectNoticeAuditLog, DefectAuditLog, ScheduleForm, CalendarPreview, PhotoGalleryModal },
  props: {
    unit: {
      required: true
    }
  },
  data() {
    return {
      dialogs: {
        showDefectScheduleEntry: false
      },
      showCalendar: false,
      showLog: false,
      showChildLog: false,
      childLogDefectId: null,
      childLogWorkSessions: null,
      childLogAssignees: null,
      childLogHistoricalAcceptedJobs: null,
      childLogDefectNumber: null,
      dateFormat: DateFormat.DefaultDisplay,
      expanded: false,
      defects: [],
      model: {},
      galleryPhotos: [],
      options: {
        itemsPerPage: 10,
        page: 1,
        sortBy: ['completedDate'],
        sortDesc: [true]
      },
      footerProps: {
        itemsPerPageOptions: [10, 50, 100]
      },
      headers: [
        { text: 'EDN', value: 'ednNumber', sortable: true },
        { text: 'Received', value: 'receivedDate', sortable: true },
        { text: 'Description', value: 'description', sortable: true },
        { text: 'Completed', value: 'completedDate', sortable: true }
      ],
      totalItems: 0
    };
  },
  computed: {
    unitId() {
      return this.$route.params.unitId;
    },
    params() {
      return {
        pageSize: this.options && this.options.itemsPerPage,
        page: this.options && this.options.page,
        unitId: this.unitId,
        orderBy: this.options.sortBy && this.options.sortBy.length >= 1 ? this.options.sortBy[0] : 'receivedDate',
        orderByDescending: this.options.sortDesc && this.options.sortDesc.length >= 1 ? this.options.sortDesc[0] : false,
        openOnly: false,
        completedOnly: true,
        includeDeleted: true
      };
    },
    disabled() {
      const g = this.unit.garageLocationId;
      return this.unit.isDeleted || (!this.userIsWorkshopManagerInGarage(g, this.userGarages) && !this.userIsDataEntryInGarage(g, this.userGarages) && !this.userIsFleetManager);
    },
    hasAnyPhotos() {
      return this.defects.some(defect => defect.hasPhotos);
    },
    querystring() {
      if (!this.params) {
        return '';
      }
      return (
        '?' +
        Object.keys(this.params)
          .filter(key => !!this.params[key])
          .map(key => key + '=' + this.params[key])
          .join('&')
      );
    }
  },
  methods: {
    onCloseAllLogs() {
      this.$bus.$emit(Event.ParentLogVisibilityChanged, { visible: false });
      this.showLog = false;
      this.showChildLog = false;
      this.showCalendar = false;
    },
    toggleLog() {
      this.$bus.$emit(Event.ParentLogVisibilityChanged, { visible: !this.showLog });
      this.showLog = !this.showLog;
      this.showCalendar = false;
    },
    toggleCalendar() {
      this.$bus.$emit(Event.ParentLogVisibilityChanged, { visible: !this.showCalendar });
      this.showCalendar = !this.showCalendar;
      this.showLog = false;
    },
    closeScheduleForm() {
      this.dialogs.showDefectScheduleEntry = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    async getCompletedDefects() {
      try {
        this.$wait.start('loading defects');
        this.$wait.end('loading data');
        const response = await this.$api.get(`units/${this.unitId}/completed-defects${this.querystring}`);
        this.defects = response.data;
      } catch (error) {
        this.$toast.error('There was an error getting defects');
        this.defects = [];
      } finally {
        this.$wait.end('loading defects');
        this.$wait.end('loading data');
      }
    },
    async getDefectsForReport() {
      try {
        this.$wait.start('loading defects');
        this.$wait.end('loading data');
        const response = await this.$api.get(`defects/completed-defects-report?unitId=${this.unitId}`);
        return response.data;
      } catch (error) {
        this.$toast.error('There was an error generating the Notice History report');
        return null;
      } finally {
        this.$wait.end('loading defects');
        this.$wait.end('loading data');
      }
    },
    classes(defect) {
      return {
        sweptForward: !!defect.sweptForwardByScheduler,
        secondary: !!defect.hasRemainingPrecompletedToFill
      };
    },
    async handleEditDefectNotice(defectNoticeId) {
      const defectNotice = await this.getDefectNotice(defectNoticeId);
      if (defectNotice) {
        this.openDefectNoticeEdit(defectNotice);
      }
    },
    openDefectNoticeEdit(defectNotice) {
      this.model = defectNotice;
      this.dialogs.showDefectScheduleEntry = true;
      this.initCalendar();
      this.showLog = false;

      setTimeout(() => this.$refs.scheduleform.init(true), 10);
    },
    async exportCsv() {
      const results = await this.getDefectsForReport();
      const data = results ? results : [];

      const rows = data.map(x => [
        x.unitNumber,
        x.edn,
        x.description,
        x.hours,
        x.kilometers,
        x.completedBy,
        x.source,
        x.category,
        x.hoursWorked,
        moment(x.createdDate).format(DateFormat.DefaultDisplay),
        moment(x.receivedDate).format(DateFormat.DefaultDisplay),
        x.scheduledDate ? moment(x.scheduledDate).format(DateFormat.DefaultDisplay) : '',
        x.completedDate ? moment(x.completedDate).format(DateFormat.DefaultDisplay) : ''
      ]);

      const csv = DownloadHelper.makeCsv(
        ['Unit', 'EDN', 'Repair Description // Completion Notes', 'Hours', 'Kms', 'Completed By', 'Source', 'Category', 'Time Taken', 'Created Date', 'Received Date', 'Scheduled Date', 'Completed Date'],
        rows
      );
      DownloadHelper.download(`${this.unit.number}_NoticeHistory_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
    },
    async initCalendar() {
      this.showCalendar = false;
      setTimeout(() => this.$refs.calendar.init(), 1);
    },
    async deleteEdn(defectNoticeId, sourceDialog) {
      if (sourceDialog == 'schedule') {
        this.dialogs.showDefectScheduleEntry = false;
      } else if (sourceDialog == 'action') {
        this.dialogs.showDefectActionEntry = false;
      }

      try {
        this.$wait.start('deleting defect notice');
        await this.$api.delete(`defect-notices/${defectNoticeId}`);
        this.$toast.success('Defect Notice deleted');
        await this.getCompletedDefects();
      } catch (err) {
        this.$toast.error('Could not delete Defect Notice');
        this.$toast.error(err);
      } finally {
        this.$wait.end('deleting defect notice');
      }
    },
    async showPhotoGallery() {
      try {
        this.$wait.start('loading photos');

        // Get all defects that have photos
        const defectsWithPhotos = this.defects.filter(defect => defect.hasPhotos && defect.id);

        if (defectsWithPhotos.length === 0) {
          this.$toast.info('No photos found for completed defects');
          return;
        }

        // Fetch individual defect details to get photo URLs
        const photoPromises = defectsWithPhotos.map(async defect => {
          try {
            const response = await this.$api.get(`defects/${defect.id}`);
            const defectDetail = response.data;

            if (defectDetail.photoBlobUri) {
              return {
                photoUrl: defectDetail.photoBlobUri,
                defect: {
                  ednNumber: defect.ednNumber,
                  receivedDate: defect.receivedDate,
                  completedDate: defect.completedDate,
                  description: defect.description,
                  notes: defect.notes,
                  completedBy: defectDetail.completedBy
                }
              };
            }
            return null;
          } catch (error) {
            return null;
          }
        });

        const photos = await Promise.all(photoPromises);
        this.galleryPhotos = photos.filter(photo => photo !== null);

        if (this.galleryPhotos.length === 0) {
          this.$toast.info('No photos found for completed defects');
          return;
        }

        // Show the gallery modal
        this.$refs.photoGalleryModal.showModal();
      } catch (error) {
        this.$toast.error('Failed to load photos');
      } finally {
        this.$wait.end('loading photos');
      }
    },
    async onChildLogVisibilityChanged(event) {
      // hide my log buttons if a child is displaying its logs
      this.childLogDefectId = event.defectId;
      this.childLogWorkSessions = event.workSessions;
      this.childLogAssignees = event.assignees;
      this.childLogHistoricalAcceptedJobs = event.historicalAcceptedJobs;
      this.childLogDefectNumber = event.defectNumber;
      this.showChildLog = event.visible;
    }
  },
  async mounted() {
    await this.getCompletedDefects();
    this.$bus.$on(Event.DefectCompleted, this.getCompletedDefects);
    this.$bus.$on(Event.DefectPhotoDeleted, this.getCompletedDefects);
    this.$bus.$on(Event.DefectSchedulesUpdated, this.getCompletedDefects);
    this.$bus.$on(Event.OpenDefectNoticeClicked, this.handleEditDefectNotice);
    this.$bus.$on(Event.ChildLogVisibilityChanged, this.onChildLogVisibilityChanged);
    this.$bus.$on(Event.CloseAllLogs, this.onCloseAllLogs);
  },
  destroyed() {
    this.$bus.$off(Event.DefectCompleted, this.getCompletedDefects);
    this.$bus.$off(Event.DefectPhotoDeleted, this.getCompletedDefects);
    this.$bus.$off(Event.DefectSchedulesUpdated, this.getCompletedDefects);
    this.$bus.$off(Event.ChildLogVisibilityChanged, this.onChildLogVisibilityChanged);
    this.$bus.$off(Event.CloseAllLogs, this.onCloseAllLogs);
    this.showLog = false;
    this.showChildLog = false;
  }
};
</script>
<style>
td {
  cursor: pointer;
}

td.hasRemainingPrecompletedToFill {
  background: green;
}
</style>
